import { GetProviderPatientTranscriptionConsentQuery } from '../../generated/graphql';

import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';

import { Icon, Text } from '@growtherapy/sprout-ui';

import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { Statsig } from '../../statsig/StatsigProvider';
import { telehealthRecordingPromptForMinorsGate } from '../../statsig/gates';
import { toast } from '../../toasts';
import { AudioRecordingState, recordingStateAtom, roomAtom } from '../../video-sdk';
import { CLIENT_CONSENT_PROMPT_TOAST_DURATION } from './constants';
import { useCanProviderUseTranscriptionCallback } from './useCanProviderUseTranscriptionCallback';

const TOAST_KEY = 'minor-recording-prompt';

/**
 * Hook that returns a callback to show a toast prompting the provider to
 * manually start recording when a minor client joins with both parties already
 * having consented to transcription. Auto-start is intentionally skipped for
 * minors due to legal requirements to confirm the presence of a parent/guardian
 * first, so this prompt reminds the provider to act, since they may be used to
 * recording starting automatically for other (adult) clients.
 */
export function useMinorRecordingPromptToastCallback() {
  const room = useAtomValue(roomAtom);
  const shouldShowMultipleParticipantTranscriptionDisabled = useShouldDisableTranscriptionForMultipleParticipants();
  const getRecordingState = useAtomCallback(useCallback((get) => get(recordingStateAtom).value, []));
  const getCanProviderUseTranscription = useCanProviderUseTranscriptionCallback();

  useEffect(
    function clearToastOnRoomDisconnectOrMultiParticipant() {
      if (!room || shouldShowMultipleParticipantTranscriptionDisabled) {
        toast.close(TOAST_KEY);
      }
    },
    [room, shouldShowMultipleParticipantTranscriptionDisabled],
  );

  return useCallback(
    (
      consentResponse: GetProviderPatientTranscriptionConsentQuery,
      appointmentShortId: string | undefined,
      isClientUserMinor: boolean,
    ) => {
      if (!Statsig.checkGate(telehealthRecordingPromptForMinorsGate)) return;
      if (!appointmentShortId || !getCanProviderUseTranscription()) return;
      if (!isClientUserMinor) return;

      const patientConsent = !!consentResponse.clientTranscriptionConsent?.isOptedIn;
      const providerConsent = !!consentResponse.providerTranscriptionConsent?.isOptedIn;

      if (!providerConsent || !patientConsent) return;
      if (getRecordingState() === AudioRecordingState.RECORDING) return;

      toast.info(
        <Text as='span'>
          Transcription does not start automatically for minors. Press the{' '}
          <span className='sr-only'>start recording</span>
          <Icon aria-hidden icon={faPlay} /> button to start recording.
        </Text>,
        {
          key: TOAST_KEY,
          timeout: CLIENT_CONSENT_PROMPT_TOAST_DURATION,
        },
      );
    },
    [getRecordingState, getCanProviderUseTranscription],
  );
}

/**
 * Hook that returns a callback to close the minor recording prompt toast, if it
 * is currently visible.
 */
export function useCloseMinorRecordingPromptToastCallback() {
  return useCallback(() => {
    toast.close(TOAST_KEY);
  }, []);
}
